/**
 * Client-side visitor analytics helpers (first-party beacon).
 */

import type { VisitorEventType } from "@/lib/analytics/types";

export type TrackMeta = Record<string, unknown>;

function readUtmFromLocation(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
} {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get("utm_source")?.trim() || undefined;
    const utm_medium = params.get("utm_medium")?.trim() || undefined;
    const utm_campaign = params.get("utm_campaign")?.trim() || undefined;
    return { utm_source, utm_medium, utm_campaign };
  } catch {
    return {};
  }
}

export function track(
  eventType: VisitorEventType,
  options?: {
    path?: string;
    meta?: TrackMeta;
  }
): void {
  if (typeof window === "undefined") return;

  const path =
    options?.path ??
    `${window.location.pathname}${window.location.search || ""}`;

  if (path.startsWith("/admin") || path.startsWith("/api")) return;

  const utm = readUtmFromLocation();
  const payload = {
    event_type: eventType,
    path,
    referrer: document.referrer || null,
    utm_source: utm.utm_source ?? null,
    utm_medium: utm.utm_medium ?? null,
    utm_campaign: utm.utm_campaign ?? null,
    meta: options?.meta ?? null,
  };

  const body = JSON.stringify(payload);

  try {
    if (typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      const sent = navigator.sendBeacon("/api/analytics/collect", blob);
      if (sent) return;
    }
  } catch {
    // fall through to fetch
  }

  void fetch("/api/analytics/collect", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => {
    // Fire-and-forget
  });
}
